import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const benefits = (t) => [
  {
    title: t("sponsoringBenefits.benefit1.title"),
    description: t("sponsoringBenefits.benefit1.description"),
    icon: "fas fa-bullhorn",
    details: [
      {
        subtitle: t("sponsoringBenefits.benefit1.details.detail1.subtitle"),
        text: t("sponsoringBenefits.benefit1.details.detail1.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit1.details.detail2.subtitle"),
        text: t("sponsoringBenefits.benefit1.details.detail2.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit1.details.detail3.subtitle"),
        text: t("sponsoringBenefits.benefit1.details.detail3.text"),
      },
    ],
  },
  {
    title: t("sponsoringBenefits.benefit2.title"),
    description: t("sponsoringBenefits.benefit2.description"),
    icon: "fas fa-users",
    details: [
      {
        subtitle: t("sponsoringBenefits.benefit2.details.detail1.subtitle"),
        text: t("sponsoringBenefits.benefit2.details.detail1.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit2.details.detail2.subtitle"),
        text: t("sponsoringBenefits.benefit2.details.detail2.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit2.details.detail3.subtitle"),
        text: t("sponsoringBenefits.benefit2.details.detail3.text"),
      },
    ],
  },
  {
    title: t("sponsoringBenefits.benefit3.title"),
    description: t("sponsoringBenefits.benefit3.description"),
    icon: "fas fa-award",
    details: [
      {
        subtitle: t("sponsoringBenefits.benefit3.details.detail1.subtitle"),
        text: t("sponsoringBenefits.benefit3.details.detail1.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit3.details.detail2.subtitle"),
        text: t("sponsoringBenefits.benefit3.details.detail2.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit3.details.detail3.subtitle"),
        text: t("sponsoringBenefits.benefit3.details.detail3.text"),
      },
    ],
  },
  {
    title: t("sponsoringBenefits.benefit4.title"),
    description: t("sponsoringBenefits.benefit4.description"),
    icon: "fas fa-camera",
    details: [
      {
        subtitle: t("sponsoringBenefits.benefit4.details.detail1.subtitle"),
        text: t("sponsoringBenefits.benefit4.details.detail1.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit4.details.detail2.subtitle"),
        text: t("sponsoringBenefits.benefit4.details.detail2.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit4.details.detail3.subtitle"),
        text: t("sponsoringBenefits.benefit4.details.detail3.text"),
      },
    ],
  },
  {
    title: t("sponsoringBenefits.benefit5.title"),
    description: t("sponsoringBenefits.benefit5.description"),
    icon: "fas fa-handshake",
    details: [
      {
        subtitle: t("sponsoringBenefits.benefit5.details.detail1.subtitle"),
        text: t("sponsoringBenefits.benefit5.details.detail1.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit5.details.detail2.subtitle"),
        text: t("sponsoringBenefits.benefit5.details.detail2.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit5.details.detail3.subtitle"),
        text: t("sponsoringBenefits.benefit5.details.detail3.text"),
      },
    ],
  },
  {
    title: t("sponsoringBenefits.benefit6.title"),
    description: t("sponsoringBenefits.benefit6.description"),
    icon: "fas fa-puzzle-piece",
    details: [
      {
        subtitle: t("sponsoringBenefits.benefit6.details.detail1.subtitle"),
        text: t("sponsoringBenefits.benefit6.details.detail1.text"),
      },
      {
        subtitle: t("sponsoringBenefits.benefit6.details.detail2.subtitle"),
        text: t("sponsoringBenefits.benefit6.details.detail2.text"),
      },
    ],
  },
];

function SponsoringBenefits() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="pt-12 pb-12 bg-slate-50/50 dark:bg-slate-900 transition-colors" id="sponsoring-benefits">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("sponsoringBenefits.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("sponsoringBenefits.description")}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {benefits(t).map((benefit, index) => {
            const isActive = activeIndex === index;
            
            return (
              <div
                key={index}
                onClick={() => toggleAccordion(index)}
                className={`bg-white dark:bg-slate-800 rounded-lg border shadow-sm p-6 cursor-pointer transition-all duration-200 ${
                  isActive
                    ? "border-primary dark:border-primary-light shadow-md ring-2 ring-primary/20 dark:ring-primary-light/20"
                    : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md"
                }`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isActive
                      ? "bg-primary dark:bg-primary-light text-white shadow-lg"
                      : "bg-primary/10 dark:bg-primary-light/20 text-primary dark:text-primary-light"
                  }`}>
                    <i className={`${benefit.icon} text-2xl`}></i>
                  </div>
                  
                  {/* Content */}
                  <div className="space-y-2 w-full">
                    <h3 className={`text-lg font-semibold ${
                      isActive
                        ? "text-primary dark:text-primary-light"
                        : "text-slate-900 dark:text-slate-100"
                    }`}>
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>

                  {/* Expand/Collapse Icon */}
                  <div className="flex-shrink-0">
                    <i className={`fas fa-chevron-${isActive ? 'up' : 'down'} text-slate-400 dark:text-slate-500 text-sm`}></i>
                  </div>
                </div>

                {/* Expanded Details */}
                {isActive && (
                  <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 space-y-4">
                    {benefit.details.map((detail, i) => (
                      <div key={i} className="space-y-2">
                        <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {detail.subtitle}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                          {detail.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Download Section */}
        <div className="flex justify-center">
          <a
            href="/documents/Event-Mappe.pdf"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 shadow-sm hover:shadow-md"
            download="GCWAA-Event-Mappe.pdf"
            rel="noopener noreferrer"
          >
            <i className="fas fa-download"></i>
            {t("sponsoringBenefits.downloadEventmappe")}
          </a>
        </div>
      </div>
    </section>
  );
}

export default SponsoringBenefits;
